import { Box, Button, LinearProgress, Typography, getContrastRatio } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CustomAlert from "./CustomAlert";
import CheckIcon from "../../../components/icons/CheckIcon";
import { generateLighterContrastColor } from "../../../utils/commonServices";
import useCustomTheme from "../../../theme/useCustomTheme";
import CustomTooltip from "../../../components/CustomTooltip";
import { removeExtension } from "../../../utils/commonServices";
import {
  BULK_DELETE_MAX_BATCH,
  GENERIC_FILE_LABEL,
  failureReasonText,
  resolveFailedFileName,
} from "../../../utils/bulkMediaDelete";

const titleSx = {
  fontWeight: 600,
  fontFamily: "Rubik",
  fontSize: { xs: "16px", sm: "16px", md: "18px", lg: "20px", xl: "30px" },
};

const noteSx = {
  fontSize: { xs: "8px", md: "10px", lg: "11px", xl: "16px" },
  fontFamily: "Rubik",
  letterSpacing: "-0.05px",
  wordWrap: "break-word",
};

const BulkDeleteMediaPopup = (props) => {
  const {
    open,
    handleClose,
    selectedCount,
    bulkDeleteStatus,
    bulkDeleteResult,
    bulkDeleteQueuedBatch,
    bulkDeleteError,
    bulkDeleteFileNames,
    onConfirm,
    onRetry,
  } = props;
  const [theme] = useCustomTheme();

  const confirmColor =
    theme.palette.primary.main === ("#00d744" || "#00D744")
      ? "#00d744"
      : getContrastRatio("#08231B", theme.palette.primary.main) > 4.5
      ? `${theme.palette.primary.main}`
      : generateLighterContrastColor(theme.palette.primary.main);

  const isBusy = bulkDeleteStatus === "deleting" || bulkDeleteStatus === "queued";
  const failed = bulkDeleteResult?.failed || [];
  const deleted = bulkDeleteResult?.deleted || [];

  const processedCount =
    (bulkDeleteQueuedBatch?.deleted_count || 0) +
    (bulkDeleteQueuedBatch?.failed_count || 0);
  const totalCount = bulkDeleteQueuedBatch?.total_count || selectedCount || 0;
  const progressValue =
    totalCount > 0 ? Math.min(100, Math.round((processedCount / totalCount) * 100)) : 0;

  const fileWord = (count) => (count === 1 ? "file" : "files");

  const confirmView = (
    <>
      <Box flexGrow={1} pt={7}>
        <Typography sx={titleSx}>
          {`Are you sure you want to delete ${selectedCount} ${fileWord(selectedCount)}?`}
        </Typography>
        <Typography sx={{ ...noteSx, display: "inline", width: "100%" }}>
          <span style={{ fontWeight: 600 }}>Note: </span>
          This is permanent. Deleted files cannot be recovered.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "end" }}>
        <Box pr={1.5}>
          {isBusy ? (
            <LoadingButton
              sx={{
                "& .MuiLoadingButton-loadingIndicator": { color: theme.palette.primary.main },
                height: { xs: "36px", sm: "40px", md: "45px", lg: "51px", xl: "75px" },
                minWidth: {
                  xs: "36px !important",
                  sm: "40px !important",
                  md: "45px !important",
                  lg: "51px !important",
                  xl: "75px !important",
                },
                backgroundColor: "#08231B",
              }}
              loading
              variant="outlined"
              disabled
            />
          ) : (
            <CheckIcon
              color={confirmColor}
              onClick={onConfirm}
              role="button"
              tabIndex={0}
              aria-label="Confirm delete"
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onConfirm()}
              sx={{
                fontSize: { xs: "40px", sm: "44px", md: "50px", lg: "56px", xl: "82px" },
                cursor: "pointer",
              }}
            />
          )}
        </Box>
        <Box>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={isBusy}
            sx={{
              fontSize: { xs: "8px", sm: "9px", md: "10px", lg: "11px", xl: "16px" },
              fontWeight: 600,
              height: { xs: "18px", sm: "20px", md: "22px", lg: "24px", xl: "32px" },
              width: { xs: "70px", sm: "70px", md: "80px", lg: "90px", xl: "120px" },
              fontFamily: "Rubik",
              textTransform: "none",
              letterSpacing: "0.65px",
              textDecoration: "underline",
            }}
          >
            Or Go Back
          </Button>
        </Box>
      </Box>
    </>
  );

  const queuedView = (
    <>
      <Box flexGrow={1} pt={7}>
        <Typography sx={titleSx}>
          {`Deleting ${totalCount} ${fileWord(totalCount)} in the background.`}
        </Typography>
        <Typography sx={{ ...noteSx, display: "block", mt: 1 }}>
          {`${processedCount} of ${totalCount} processed. You can keep this open — we'll show the result here.`}
        </Typography>
        <Box sx={{ mt: 2, maxWidth: "80%" }}>
          <LinearProgress variant="determinate" value={progressValue} />
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }} pr={1.5}>
        <LoadingButton
          sx={{
            "& .MuiLoadingButton-loadingIndicator": { color: theme.palette.primary.main },
            height: { xs: "36px", sm: "40px", md: "45px", lg: "51px", xl: "75px" },
            minWidth: {
              xs: "36px !important",
              sm: "40px !important",
              md: "45px !important",
              lg: "51px !important",
              xl: "75px !important",
            },
            backgroundColor: "#08231B",
          }}
          loading
          variant="outlined"
          disabled
        />
      </Box>
    </>
  );

  const resultView = (
    <>
      <Box flexGrow={1} pt={4} sx={{ overflowY: "auto" }}>
        <Typography sx={titleSx}>
          {failed.length === 0
            ? `${deleted.length} ${fileWord(deleted.length)} deleted.`
            : deleted.length === 0
            ? `No files were deleted.`
            : `${deleted.length} ${fileWord(deleted.length)} deleted, ${failed.length} couldn't be deleted.`}
        </Typography>

        {failed.length > 0 && (
          <Box mt={2} sx={{ maxHeight: { xs: "90px", md: "120px", xl: "200px" }, overflowY: "auto", pr: 2 }}>
            {failed.map((file) => {
              // BMD-01/BMD-04: name is withheld for anything the caller may not
              // see, so fall back to the selection snapshot and then to a
              // generic label. Never render the raw null.
              const fileName = resolveFailedFileName(file, bulkDeleteFileNames);
              const fileLabel = fileName ? removeExtension(fileName) : GENERIC_FILE_LABEL;
              return (
              <Box
                key={file?.id}
                sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}
              >
                <CustomTooltip title={fileName || GENERIC_FILE_LABEL}>
                  <Typography
                    sx={{
                      ...noteSx,
                      fontWeight: 600,
                      maxWidth: "50%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {fileLabel}
                  </Typography>
                </CustomTooltip>
                <Typography sx={{ ...noteSx, textAlign: "right" }}>
                  {failureReasonText(file?.reason)}
                </Typography>
              </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }} pr={1.5}>
        <CheckIcon
          color={confirmColor}
          onClick={handleClose}
          sx={{
            fontSize: { xs: "40px", sm: "44px", md: "50px", lg: "56px", xl: "82px" },
            cursor: "pointer",
          }}
        />
      </Box>
    </>
  );

  const errorView = (
    <>
      <Box flexGrow={1} pt={7}>
        <Typography sx={titleSx}>Those files couldn't be deleted.</Typography>
        <Typography sx={{ ...noteSx, display: "block", mt: 1 }}>{bulkDeleteError}</Typography>
        <Typography sx={{ ...noteSx, display: "block", mt: 1 }}>
          {`You can select up to ${BULK_DELETE_MAX_BATCH} files at a time.`}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "end" }}>
        <Box pr={1.5}>
          <Button
            variant="contained"
            onClick={onRetry}
            sx={{
              fontFamily: "Rubik",
              textTransform: "none",
              fontSize: { xs: "9px", md: "10px", lg: "11px", xl: "16px" },
            }}
          >
            Try Again
          </Button>
        </Box>
        <Box>
          <Button
            variant="text"
            onClick={handleClose}
            sx={{
              fontSize: { xs: "8px", sm: "9px", md: "10px", lg: "11px", xl: "16px" },
              fontWeight: 600,
              fontFamily: "Rubik",
              textTransform: "none",
              letterSpacing: "0.65px",
              textDecoration: "underline",
            }}
          >
            Or Go Back
          </Button>
        </Box>
      </Box>
    </>
  );

  return (
    <CustomAlert
      open={open}
      handleClose={isBusy ? undefined : handleClose}
      hideCloseHandler={isBusy}
      height="400px"
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }} px={3} pt={3} pb={2}>
        {bulkDeleteStatus === "queued"
          ? queuedView
          : bulkDeleteStatus === "done"
          ? resultView
          : bulkDeleteStatus === "error"
          ? errorView
          : confirmView}
      </Box>
    </CustomAlert>
  );
};

export default BulkDeleteMediaPopup;
